/**
 * Onboarding-wizard telemetry contract: the typed vocabulary the shared
 * analytics framework uses to own the first-launch wizard's funnel (plan ONB).
 *
 * WHY THIS FILE EXISTS — the wizard shipped its own second telemetry framework
 * (`onboarding/telemetry.ts` + `usePostHog().capture` at five screen call
 * sites), which bypasses `track()` entirely: no event-name validation, no
 * context envelope, no provider fan-out, and — the defect this file closes —
 * its own name for stages the shared framework ALREADY emits canonically, so a
 * single user action was counted twice under two different names.
 *
 * The reconciliation has exactly two outcomes per legacy `onboarding_*` name,
 * both declared in {@link ONBOARDING_EVENT_OWNERSHIP}:
 *
 *  - `onboarding_wizard` — the behaviour is wizard progression that nothing
 *    else describes. The shared framework takes it over under a canonical
 *    name and dual-emits the legacy name through the parity window.
 *  - `shared_framework`  — the behaviour is a journey-stage outcome an existing
 *    canonical event already reports. The wizard must NOT emit it at all; the
 *    canonical event is the single source of the count.
 *
 * The second class is enforced, not just documented: superseded names are
 * deliberately absent from `EVENT_NAMES`, so they fail `tsc` at any call site
 * AND are rejected at runtime by `validateEvent` ("Invalid event name"). That
 * is what makes "which framework wins" a build error instead of a convention.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global). Canonical naming
 * convention + dual-write-first migration: canonical-event-map.json.
 */

import { POSTHOG_EXCEPTION_PROPERTY_NAMES } from '#~/posthog/event-names.js';
import { EVENT_NAMES, type EventName } from './event-names.js';

/**
 * Journey stages of the desktop onboarding funnel that the wizard owns. These
 * are the coarse funnel dimension a PM builds drop-off on; `client` (attached
 * by the analytics context) separates the electron and standalone-web funnels
 * without blending them, so ONE stage dimension serves both.
 */
export const ONBOARDING_STAGE = {
  WELCOME: 'welcome',
  LINK_BRIDGE: 'link_bridge',
  DISCOVER: 'discover',
  ADD_CAMERA: 'add_camera',
  FIRST_STREAM: 'first_stream',
  COMPLETE: 'complete'
} as const;

export type OnboardingStage = (typeof ONBOARDING_STAGE)[keyof typeof ONBOARDING_STAGE];

/**
 * The wizard's individual screens/steps inside a stage. Carried as a property
 * rather than baked into event names: eight per-screen event names collapse to
 * one `onboarding_step_viewed` + this dimension, so the catalog does not grow a
 * near-duplicate event per screen and a funnel is built on one event.
 */
export const ONBOARDING_STEP = {
  WELCOME: 'welcome',
  BRIDGE_CONCEPT: 'bridge_concept',
  BRIDGE_DETECT: 'bridge_detect',
  CHOOSE_METHOD: 'choose_method',
  DISCOVERY: 'discovery',
  CAMERA_FORM: 'camera_form',
  CONNECTION_TEST: 'connection_test',
  MAGIC_MOMENT: 'magic_moment',
  COMPLETE: 'complete'
} as const;

export type OnboardingStep = (typeof ONBOARDING_STEP)[keyof typeof ONBOARDING_STEP];

/**
 * Severity of an onboarding failure, using PostHog's `$exception_level`
 * taxonomy so a PM filters fatal from noise in the SAME field the PostHog
 * exception UI already uses.
 * - `fatal`   — the journey is dead at this stage and cannot recover (the
 *               bridge never connects, so no camera can be added at all).
 * - `error`   — a real failure the user can retry or fix.
 * - `warning` — expected friction, not a defect (needs credentials, plan limit).
 */
export const ONBOARDING_ERROR_SEVERITY = {
  FATAL: 'fatal',
  ERROR: 'error',
  WARNING: 'warning'
} as const;

export type OnboardingErrorSeverity =
  (typeof ONBOARDING_ERROR_SEVERITY)[keyof typeof ONBOARDING_ERROR_SEVERITY];

/**
 * Failure categories. Order of detection matters in
 * `classifyOnboardingError` (domains/onboarding-lifecycle.ts): a policy block
 * is checked before transport failures so a plan limit is never reported as a
 * network timeout.
 */
export const ONBOARDING_ERROR_CATEGORY = {
  BRIDGE_NEVER_CONNECTED: 'bridge_never_connected',
  BRIDGE_NOT_READY: 'bridge_not_ready',
  PLAN_LIMIT: 'plan_limit',
  TIMEOUT: 'timeout',
  AUTH_REQUIRED: 'auth_required',
  UNREACHABLE: 'unreachable',
  UNKNOWN: 'unknown'
} as const;

export type OnboardingErrorCategory =
  (typeof ONBOARDING_ERROR_CATEGORY)[keyof typeof ONBOARDING_ERROR_CATEGORY];

/** How the user reached the camera being added — the wizard's fork dimension. */
export const ONBOARDING_ADD_METHOD = {
  MANUAL: 'manual',
  DISCOVERY: 'discovery'
} as const;

export type OnboardingAddMethod =
  (typeof ONBOARDING_ADD_METHOD)[keyof typeof ONBOARDING_ADD_METHOD];

/**
 * Property keys carried by onboarding events. `$exception_level` is reused from
 * the PostHog-owned constants rather than re-spelled here so the wizard's
 * failures land in the same field the exception pipeline uses.
 *
 * Deliberately absent: the generic `severity` key. It already carries the
 * `S3/warn` alerting taxonomy (EVENT_SEVERITY in event-properties.ts); writing
 * fatal/error/warning into the same key would make one property hold two
 * incompatible vocabularies.
 */
export const ONBOARDING_PROPERTIES = {
  STAGE: 'onboarding_stage',
  STEP: 'onboarding_step',
  ADD_METHOD: 'add_method',
  EXCEPTION_LEVEL: POSTHOG_EXCEPTION_PROPERTY_NAMES.LEVEL,
  EXCEPTION_SOURCE: POSTHOG_EXCEPTION_PROPERTY_NAMES.SOURCE,
  RECOVERABLE: 'recoverable',
  TERMINAL: 'terminal'
} as const;

/** `$exception_source` value marking a failure as raised by the wizard. */
export const ONBOARDING_EXCEPTION_SOURCE = 'onboarding';

/**
 * The canonical onboarding event names, composed by the typed `eventName`
 * builder in event-names.ts. Grouped here so tests, the catalog check and the
 * tracker all read one list instead of re-enumerating literals.
 */
export const ONBOARDING_CANONICAL_EVENT_NAMES = {
  STARTED: EVENT_NAMES.ONBOARDING_STARTED,
  SUCCEEDED: EVENT_NAMES.ONBOARDING_SUCCEEDED,
  SKIPPED: EVENT_NAMES.ONBOARDING_SKIPPED,
  FAILED: EVENT_NAMES.ONBOARDING_FAILED,
  RECOVERED: EVENT_NAMES.ONBOARDING_RECOVERED,
  STEP_VIEWED: EVENT_NAMES.ONBOARDING_STEP_VIEWED,
  STEP_STARTED: EVENT_NAMES.ONBOARDING_STEP_STARTED,
  STEP_SUBMITTED: EVENT_NAMES.ONBOARDING_STEP_SUBMITTED
} as const;

export type OnboardingCanonicalEventName =
  (typeof ONBOARDING_CANONICAL_EVENT_NAMES)[keyof typeof ONBOARDING_CANONICAL_EVENT_NAMES];

/**
 * Legacy wizard names kept alive ONLY for the dual-emit parity window. Values
 * alias the EVENT_NAMES literals — one source of truth, zero drift — so the
 * retirement ticket deletes this map plus the EVENT_NAMES twins rather than
 * hunting call sites (the ENG-4340 contract's dual_emit_rule).
 */
export const ONBOARDING_LEGACY_EVENT_NAMES = {
  FUNNEL_STARTED: EVENT_NAMES.ONBOARDING_FUNNEL_STARTED,
  WELCOME_VIEWED: EVENT_NAMES.ONBOARDING_WELCOME_VIEWED,
  BRIDGE_CONCEPT_VIEWED: EVENT_NAMES.ONBOARDING_BRIDGE_CONCEPT_VIEWED,
  BRIDGE_DETECT_STARTED: EVENT_NAMES.ONBOARDING_BRIDGE_DETECT_STARTED,
  CHOOSE_METHOD_VIEWED: EVENT_NAMES.ONBOARDING_CHOOSE_METHOD_VIEWED,
  METHOD_CHOSEN: EVENT_NAMES.ONBOARDING_METHOD_CHOSEN,
  DISCOVERY_STARTED: EVENT_NAMES.ONBOARDING_DISCOVERY_STARTED,
  CAMERA_FORM_VIEWED: EVENT_NAMES.ONBOARDING_CAMERA_FORM_VIEWED,
  CAMERA_SUBMITTED: EVENT_NAMES.ONBOARDING_CAMERA_SUBMITTED,
  CONNECTION_TEST_STARTED: EVENT_NAMES.ONBOARDING_CONNECTION_TEST_STARTED,
  MAGIC_MOMENT_REACHED: EVENT_NAMES.ONBOARDING_MAGIC_MOMENT_REACHED,
  COMPLETE_REACHED: EVENT_NAMES.ONBOARDING_COMPLETE_REACHED,
  COMPLETED: EVENT_NAMES.ONBOARDING_COMPLETED,
  BRIDGE_CONNECT_RECOVERED: EVENT_NAMES.ONBOARDING_BRIDGE_CONNECT_RECOVERED,
  ERROR: EVENT_NAMES.ONBOARDING_ERROR
} as const;

export type OnboardingLegacyEventName =
  (typeof ONBOARDING_LEGACY_EVENT_NAMES)[keyof typeof ONBOARDING_LEGACY_EVENT_NAMES];

/**
 * Wizard names that ALREADY matched the canonical convention, so they are
 * adopted as-is rather than renamed. They must never be dual-emitted: the
 * canonical and legacy names are the same string, and emitting it twice would
 * double the count.
 */
export const ONBOARDING_UNCHANGED_EVENT_NAMES = {
  SKIPPED: EVENT_NAMES.ONBOARDING_SKIPPED
} as const;

export type OnboardingUnchangedEventName =
  (typeof ONBOARDING_UNCHANGED_EVENT_NAMES)[keyof typeof ONBOARDING_UNCHANGED_EVENT_NAMES];

/** A name the tracker may emit as the legacy leg of a dual-write. */
export type OnboardingDualEmitCandidateName =
  | OnboardingLegacyEventName
  | OnboardingUnchangedEventName;

/**
 * Wizard names whose behaviour an existing canonical event already reports.
 * These are raw literals ON PURPOSE: they are NOT in `EVENT_NAMES`, because
 * adding them would hand a call site the ability to emit the duplicate this
 * ticket exists to remove. They appear here so the catalog, the retirement
 * ticket and the wizard's authors can see where each count moved.
 */
export const ONBOARDING_SUPERSEDED_EVENT_NAMES = {
  BRIDGE_CONNECTED: 'onboarding_bridge_connected',
  DISCOVERY_RESULT_COUNT: 'onboarding_discovery_result_count',
  DISCOVERY_CAMERA_PICKED: 'onboarding_discovery_camera_picked',
  CONNECTION_RESULT: 'onboarding_connection_result',
  FIRST_PTZ_ACTION: 'onboarding_first_ptz_action',
  FIRST_STREAM: 'onboarding_first_stream'
} as const;

export type OnboardingSupersededEventName =
  (typeof ONBOARDING_SUPERSEDED_EVENT_NAMES)[keyof typeof ONBOARDING_SUPERSEDED_EVENT_NAMES];

/** Every wizard event name the reconciliation has to account for. */
export type OnboardingWizardLegacyEventName =
  | OnboardingLegacyEventName
  | OnboardingUnchangedEventName
  | OnboardingSupersededEventName;

/**
 * A legacy name taken over by the shared framework under `canonical`. `step`
 * is present when the canonical name is step-scoped: the (canonical, step) pair
 * is what identifies which legacy twin to dual-emit.
 */
interface OnboardingOwnedByWizard {
  readonly owner: 'onboarding_wizard';
  readonly canonical: OnboardingCanonicalEventName;
  readonly step?: OnboardingStep;
}

/**
 * A legacy name that must not be emitted: `supersededBy` names the canonical
 * event(s) that already count this behaviour. `derivation` records how the
 * wizard-specific nuance (a count, a first-ness) is recovered from them, so
 * dropping the duplicate does not silently drop a metric.
 */
interface OnboardingOwnedByFramework {
  readonly owner: 'shared_framework';
  readonly supersededBy: readonly EventName[];
  readonly derivation: string;
}

export type OnboardingEventOwnership = OnboardingOwnedByWizard | OnboardingOwnedByFramework;

/**
 * The reconciliation table — one entry per wizard event name, exhaustive by
 * construction (`satisfies Record<OnboardingWizardLegacyEventName, …>` fails
 * the build if a name is added without deciding who owns it).
 */
export const ONBOARDING_EVENT_OWNERSHIP = {
  [ONBOARDING_LEGACY_EVENT_NAMES.FUNNEL_STARTED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STARTED
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.WELCOME_VIEWED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.WELCOME
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.BRIDGE_CONCEPT_VIEWED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.BRIDGE_CONCEPT
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.CHOOSE_METHOD_VIEWED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.CHOOSE_METHOD
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.CAMERA_FORM_VIEWED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.CAMERA_FORM
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.MAGIC_MOMENT_REACHED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.MAGIC_MOMENT
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.COMPLETE_REACHED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_VIEWED,
    step: ONBOARDING_STEP.COMPLETE
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.BRIDGE_DETECT_STARTED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_STARTED,
    step: ONBOARDING_STEP.BRIDGE_DETECT
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.DISCOVERY_STARTED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_STARTED,
    step: ONBOARDING_STEP.DISCOVERY
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.CONNECTION_TEST_STARTED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_STARTED,
    step: ONBOARDING_STEP.CONNECTION_TEST
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.METHOD_CHOSEN]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_SUBMITTED,
    step: ONBOARDING_STEP.CHOOSE_METHOD
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.CAMERA_SUBMITTED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.STEP_SUBMITTED,
    step: ONBOARDING_STEP.CAMERA_FORM
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.COMPLETED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.SUCCEEDED
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.BRIDGE_CONNECT_RECOVERED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.RECOVERED
  },
  [ONBOARDING_LEGACY_EVENT_NAMES.ERROR]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.FAILED
  },
  [ONBOARDING_UNCHANGED_EVENT_NAMES.SKIPPED]: {
    owner: 'onboarding_wizard',
    canonical: ONBOARDING_CANONICAL_EVENT_NAMES.SKIPPED
  },
  // The linked-bridge terminal already has a canonical owner; the wizard's copy
  // inflated the link-bridge stage by one event per first launch.
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.BRIDGE_CONNECTED]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.BRIDGE_LINK_SUCCEEDED],
    derivation: 'Bridge-link stage entry count comes from the auto-link terminal.'
  },
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.DISCOVERY_RESULT_COUNT]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.DISCOVERY_DEVICE_FOUND, EVENT_NAMES.DISCOVERY_EMPTY],
    derivation: 'Result count = discovery_device_found per session; zero results = discovery_empty.'
  },
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.DISCOVERY_CAMERA_PICKED]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.DEVICE_CLICK_DISCOVERED],
    derivation: 'Picking a discovered camera is already the device_click_discovered event.'
  },
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.CONNECTION_RESULT]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.DEVICE_ADD, ONBOARDING_CANONICAL_EVENT_NAMES.FAILED],
    derivation:
      'Success leg is the device_add activation event; failure leg is onboarding_failed with error_type.'
  },
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.FIRST_PTZ_ACTION]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.DEVICE_MOVE_DIRECT, EVENT_NAMES.DEVICE_MOVE_START],
    derivation:
      'First-ness = the first move event for a device_id after its device_add, the same rule the journey spec uses for first stream.'
  },
  [ONBOARDING_SUPERSEDED_EVENT_NAMES.FIRST_STREAM]: {
    owner: 'shared_framework',
    supersededBy: [EVENT_NAMES.MEDIA_STREAM_SUCCEEDED],
    derivation:
      'First stream = the first streaming-success for a device_id after its device_add, deduped.'
  }
} as const satisfies Record<OnboardingWizardLegacyEventName, OnboardingEventOwnership>;

/** Narrow a raw ownership-table key back to a dual-emit candidate name. */
const isDualEmitCandidateName = (name: string): name is OnboardingDualEmitCandidateName =>
  Object.values(ONBOARDING_LEGACY_EVENT_NAMES).some((legacy) => legacy === name) ||
  Object.values(ONBOARDING_UNCHANGED_EVENT_NAMES).some((unchanged) => unchanged === name);

/** Composite key for the twin lookup; the separator cannot occur in a name. */
const twinKey = (canonical: OnboardingCanonicalEventName, step?: OnboardingStep): string =>
  `${canonical}|${step ?? ''}`;

/**
 * (canonical, step) -> legacy twin, DERIVED from the ownership table above so
 * the dual-emit pairing can never drift from the reconciliation decision.
 * Step-less canonical names key on the canonical name with an empty step.
 */
const LEGACY_TWIN_BY_CANONICAL_STEP: ReadonlyMap<string, OnboardingDualEmitCandidateName> = (() => {
  const twins = new Map<string, OnboardingDualEmitCandidateName>();
  for (const [legacyName, ownership] of Object.entries(ONBOARDING_EVENT_OWNERSHIP)) {
    if (ownership.owner !== 'onboarding_wizard') continue;
    if (!isDualEmitCandidateName(legacyName)) continue;
    // Step-less entries (the wizard-wide terminals) have no `step` key at all,
    // so read it through an `in` narrow rather than assuming the property exists.
    const step = 'step' in ownership ? ownership.step : undefined;
    twins.set(twinKey(ownership.canonical, step), legacyName);
  }
  return twins;
})();

/**
 * The legacy name to dual-emit beside `canonical` for `step`, or `undefined`
 * when there is nothing to dual-emit.
 *
 * Returns `undefined` when the legacy and canonical names are IDENTICAL
 * (`onboarding_skipped` already matched the naming convention, so it was not
 * renamed). Without that guard the tracker would emit the same name twice and
 * double the very count this reconciliation exists to fix.
 */
export const resolveLegacyOnboardingEventName = (
  canonical: OnboardingCanonicalEventName,
  step?: OnboardingStep
): OnboardingDualEmitCandidateName | undefined => {
  const legacy =
    LEGACY_TWIN_BY_CANONICAL_STEP.get(twinKey(canonical, step)) ??
    LEGACY_TWIN_BY_CANONICAL_STEP.get(twinKey(canonical));
  return legacy === undefined || legacy === canonical ? undefined : legacy;
};

/** Every onboarding name the shared framework is allowed to emit. */
export const ONBOARDING_EMITTABLE_EVENT_NAMES: readonly (
  | OnboardingCanonicalEventName
  | OnboardingDualEmitCandidateName
)[] = [
  ...Object.values(ONBOARDING_CANONICAL_EVENT_NAMES),
  ...Object.values(ONBOARDING_LEGACY_EVENT_NAMES),
  ...Object.values(ONBOARDING_UNCHANGED_EVENT_NAMES)
];

/**
 * Narrow an arbitrary event name to one the onboarding domain owns. Used by the
 * e2e test probe to construct a typed onboarding event without a type
 * assertion, and by tests to prove superseded names are not emittable.
 */
export const isOnboardingEventName = (
  name: string
): name is OnboardingCanonicalEventName | OnboardingDualEmitCandidateName =>
  ONBOARDING_EMITTABLE_EVENT_NAMES.some((emittable) => emittable === name);
